import React from "react";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  NumberField,
  Filter,
  TextInput,
  CheckboxGroupInput,
  DateTimeInput,
  maxValue,
  useListContext,
  required,
} from "react-admin";
import { Box, Typography, Alert, AlertTitle } from "@mui/material";
import DocumentRedownloadButton from "../components/DocumentRedownloadButton";
import { IALoading } from "../components/IAProgress";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";

const StyledErrorMessage = styled(Typography)(({ theme }) => ({
  margin: "35px",
  padding: "20px",
  textAlign: "center",
  color: theme.palette.mode === "dark" ? "#f87171" : "#dc2626",
  backgroundColor: theme.palette.mode === "dark" ? "#2d1b1b" : "#fee2e2",
  borderRadius: 2,
}));
const StyledTextInput = styled(TextInput)({
  width: 175,
  borderRadius: 1,
});

const now = new Date().getTime();
const maxDate = new Date(now - (now % 86400000) + 86400000);
const validate_start = [required()];
const validate_end = [required(), maxValue(maxDate)];

const DocumentsFilter = (props) => (
  <Filter {...props} sx={{ marginBottom: 2 }}>
    <StyledTextInput
      label="Customer UUID/Key"
      source="uuid"
      InputLabelProps={{ sx: { color: (theme) => theme.palette.text.secondary } }}
      alwaysOn
    />
    <StyledTextInput
      label="Sender Email"
      source="sender_email"
      InputLabelProps={{ sx: { color: (theme) => theme.palette.text.secondary } }}
      alwaysOn
    />
    <DateTimeInput label="Start Date" source="start_date" validate={validate_start} sx={{ width: 175 }} alwaysOn />
    <DateTimeInput label="End Date" source="end_date" validate={validate_end} sx={{ width: 175 }} alwaysOn />
    <CheckboxGroupInput
      source="downloaded_documents"
      choices={[{ id: "true", name: "Only Downloaded Documents" }]}
      sx={{ color: (theme) => theme.palette.text.primary }}
      alwaysOn
    />
  </Filter>
);

const ListSummary = () => {
  const { total, filterValues } = useListContext();
  return (
    <div style={{ padding: "8px 16px", background: "#f5f5f5" }}>
      Showing <strong>{total}</strong> results.
    </div>
  );
};

const CustomDocumentsList = (props) => {
  const { filterValues, isLoading } = useListContext(props);
  const documentListError = localStorage.getItem("documentListError");
  const navigate = useNavigate();

  const handleRowClick = (id, resource, record) => {
    navigate(`/documents/${id}/show`);
    return false;
  };

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", margin: "35px" }}>
        <IALoading /> <Typography variant="h6">Loading documents...</Typography>
      </Box>
    );
  }

  if (documentListError != null) {
    localStorage.removeItem("documentListError");
    switch (documentListError) {
      case "Invalid Sender Email":
        return <StyledErrorMessage variant="h6">Error 404: Invalid Driver Email</StyledErrorMessage>;
      case "Invalid Customer UUID":
        return <StyledErrorMessage variant="h6">Error 404: Invalid Customer UUID</StyledErrorMessage>;
      case "Invalid Client Key":
        return <StyledErrorMessage variant="h6">Error 404: Invalid Client Key</StyledErrorMessage>;
      case "Internal Server Error":
        return <StyledErrorMessage variant="h6">Error 500: Internal Server Error</StyledErrorMessage>;
      case "Date Range Too Large":
        return (
          <React.Fragment>
            <StyledErrorMessage variant="h6">Error 400: Date Range Too Large</StyledErrorMessage>
            <StyledErrorMessage variant="h6">Date Range Must Be 30 Days</StyledErrorMessage>
          </React.Fragment>
        );
      case "Bad Request":
        return <StyledErrorMessage variant="h6">Error 400: Bad Request</StyledErrorMessage>;
      default:
        return (
          <StyledErrorMessage
            variant="h6"
            sx={{ color: (theme) => (theme.palette.mode === "dark" ? "#94a3b8" : "#475569") }}
          >
            No documents found
          </StyledErrorMessage>
        );
    }
  } else if (!filterValues.sender_email && !filterValues.uuid) {
    return (
      <Box
        sx={{
          textAlign: "center",
          margin: "35px",
          padding: "20px",
          backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#1f2a44" : "#f1f5f9"),
          borderRadius: 2,
          color: (theme) => theme.palette.text.primary,
        }}
      >
        <Typography variant="h6">Fill one or more of the fields to retrieve documents</Typography>
        <Typography variant="h6">Use Customer ID if you want to mark documents for redownload</Typography>
      </Box>
    );
  }
  return (
    <Datagrid
      key={props.key}
      bulkActionButtons={filterValues.uuid ? <DocumentRedownloadButton /> : false}
      size="small"
      rowClick={handleRowClick}
      sx={{
        "& .RaDatagrid-headerCell": {
          backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#374151" : "#f1f5f9"),
          fontWeight: 600,
          color: (theme) => theme.palette.text.primary,
        },
        "& .RaDatagrid-row": {
          cursor: "pointer",
          "&:hover": {
            backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#2d3748" : "#f9fafb"),
          },
        },
        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#1f2a44" : "#fff"),
      }}
    >
      <TextField source="document_uuid" />
      <DateField source="created_at" showTime options={{ timeZone: "UTC", timeZoneName: "short" }} />
      <TextField source="driver_email" />
      <NumberField source="num_pages" label="Number of pages" textAlign="center" />
    </Datagrid>
  );
};

export const DocumentsList = (props) => {
  const curr = new Date();
  const start = new Date();
  start.setDate(curr.getDate() - 30);

  return (
    <>
      <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
        <AlertTitle>
          <strong>Limitations</strong>
        </AlertTitle>
        <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
          <li>
            {" "}
            <strong>Intended Use</strong>
            <div></div>
            This tool is only intended to mark a few specific documents for redownload. For batches that span a
            timeframe of more than a day or have more than 50 documents, please contact Tier 2 Support.
          </li>
          <li>
            {" "}
            <strong>Notes about 'Only Downloaded Documents' filter</strong>
            <div></div>
            The <em>only downloaded documents</em> filter looks at the first 50 documents in your timeframe and flags
            which of those have been downloaded. If it shows zero downloaded documents, that doesn't mean all documents
            have been downloaded — it just means the first 50 have not been downloaded. Documents beyond position 50 are
            not checked. For the most consistent results, use very short timeframes, like 1-5 minutes.
          </li>
        </ul>
      </Alert>
      <List
        {...props}
        pagination={false}
        bulkActionButtons={false}
        filters={<DocumentsFilter />}
        exporter={true}
        filterDefaultValues={{ start_date: start, end_date: curr }}
      >
        <ListSummary />
        <CustomDocumentsList />
      </List>
    </>
  );
};
